import React, { useState, useEffect } from "react";
import { Download, Filter, Calendar, List } from "react-feather";
import { Table, Button, Dropdown, Menu, Popover } from "antd";
import SelectComponent from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Swal from "sweetalert2";
import axios from "axios";
import exportService from "../../utils/exportService";
import PrintReportPreview from "../../reports/PrintReportPreview";
import { renderDate } from "../../utils/dateFormatter";
import { Line } from "react-chartjs-2";

const RoomStatusReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const business_date = localStorage.getItem("business_date");
    const start = business_date ? moment(business_date).startOf("month") : moment().startOf("month");
    return {
      startDate: start,
      endDate: moment(business_date).endOf("month"),
    };
  });
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

  const [companyId, setCompanyId] = useState(61);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle, setReportTitle] = useState(t("room_status_report"));

  const [filters, setFilters] = useState({
    reportId: "",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiURL}/reports-daily/room-status-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.startDate.format("YYYY-MM-DD"),
          endDate: dateRange.endDate.format("YYYY-MM-DD"),
          companyid: companyId,
        },
      });
      if (response.data.success) {
        const reportsWithOccupancy = response.data.reports.map(report => ({
          ...report,
          occupancy_percentage: report.occupied_rooms > 0 && (report.occupied_rooms + report.vacant_rooms + report.maintenance_rooms) > 0
            ? ((report.occupied_rooms / (report.occupied_rooms + report.vacant_rooms + report.maintenance_rooms)) * 100).toFixed(2)
            : "0.00",
        }));
        setReports(reportsWithOccupancy);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error("Error fetching room status report:", error);
      if (error.response && error.response.status === 404) {
        setReports([]);
      } else {
        Swal.fire({
          title: t("error"),
          text: t("failed_fetch_room_status_report") || "Failed to fetch room status report",
          icon: "error",
        });
        setReports([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.startDate, dateRange.endDate, companyId]);

  const handleDateRangeChange = (event, picker) => {
    const newStartDate = moment(picker.startDate).tz("Asia/Kolkata").startOf("day");
    const newEndDate = moment(picker.endDate).tz("Asia/Kolkata").endOf("day");
    if (!newStartDate.isValid() || !newEndDate.isValid()) {
      Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
      return;
    }
    if (newStartDate.isAfter(newEndDate)) {
      Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
      return;
    }
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const handleExport = (format) => {
    if (!reports.length && format !== "print") {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }

    const exportData = reports.map(report => ({
      report_date: renderDate(report.report_date),
      occupied_rooms: report.occupied_rooms,
      vacant_rooms: report.vacant_rooms,
      maintenance_rooms: report.maintenance_rooms,
      occupancy_percentage: `${report.occupancy_percentage}%`,
    }));
    const exportHeaders = [
      { key: "report_date", label: t("report_date") },
      { key: "occupied_rooms", label: t("occupied_rooms") },
      { key: "vacant_rooms", label: t("vacant_rooms") },
      { key: "maintenance_rooms", label: t("maintenance_rooms") },
      { key: "occupancy_percentage", label: t("occupancy_percentage") },
    ];
    const exportFooter = [
      { key: "total", value: t("total") },
      { key: "occupied_rooms", value: reports.reduce((sum, r) => sum + r.occupied_rooms, 0) },
      { key: "vacant_rooms", value: reports.reduce((sum, r) => sum + r.vacant_rooms, 0) },
      { key: "maintenance_rooms", value: reports.reduce((sum, r) => sum + r.maintenance_rooms, 0) },
      { key: "occupancy_percentage", value: `${
        ((reports.reduce((sum, r) => sum + r.occupied_rooms, 0) /
          (reports.reduce((sum, r) => sum + r.occupied_rooms, 0) +
            reports.reduce((sum, r) => sum + r.vacant_rooms, 0) +
            reports.reduce((sum, r) => sum + r.maintenance_rooms, 0))) * 100 || 0
        ).toFixed(2)}%` },
    ];

    const filename = `room_status_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}`;
    if (format === "xls") {
      exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, reportTitle);
    } else if (format === "csv") {
      exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    } else if (format === "pdf") {
      exportService.exportToPDF(
        exportData,
        exportHeaders,
        reportTitle,
        `${filename}.pdf`,
        false,
        true,
        exportFooter,
        exportFooter
      );
    } else if (format === "preview") {
      setExportData(exportData);
      setExportHeaders(exportHeaders);
      setExportFooter(exportFooter);
      setTotalsForPageFooter(exportFooter);
      setTotalColumns(["occupied_rooms", "vacant_rooms", "maintenance_rooms", "occupancy_percentage"]);
      setPreviewVisible(true);
    } else if (format === "print") {
      window.print();
    }
  };

  const filterContent = (
    <div style={{ width: "400px", padding: "10px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("report_id")}</label>
        <input
          type="text"
          className="form-control"
          value={filters.reportId}
          onChange={(e) => setFilters({ ...filters, reportId: e.target.value })}
          placeholder={t("search_report_id")}
        />
      </div>
    </div>
  );

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>
        {t("export_xls")}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>
        {t("export_csv")}
      </Menu.Item>

      <Menu.Item key="preview" onClick={() => handleExport("preview")}>
        {t("preview")}
      </Menu.Item>

    </Menu>
  );

  const totalOccupiedRooms = reports.reduce((sum, r) => sum + (r.occupied_rooms || 0), 0);
  const totalVacantRooms = reports.reduce((sum, r) => sum + (r.vacant_rooms || 0), 0);
  const totalMaintenanceRooms = reports.reduce((sum, r) => sum + (r.maintenance_rooms || 0), 0);
  const totalDays = reports.length;
  const averageOccupancyRate = totalDays > 0
    ? ((totalOccupiedRooms / (totalOccupiedRooms + totalVacantRooms + totalMaintenanceRooms)) * 100).toFixed(2)
    : "0.00";

  const activityStats = [
    { label: t("total_occupied_rooms"), value: totalOccupiedRooms, color: "#28a745" },
    { label: t("total_vacant_rooms"), value: totalVacantRooms, color: "#007bff" },
    { label: t("total_maintenance_rooms"), value: totalMaintenanceRooms, color: "#dc3545" },
    { label: t("average_occupancy_rate"), value: `${averageOccupancyRate}%`, color: "#17a2b8" },
  ];

  const lineChartData = {
    labels: reports.map((report) => moment(report.report_date).format("DD MMM")),
    datasets: [
      {
        label: t("occupied_rooms"),
        data: reports.map((report) => report.occupied_rooms || 0),
        borderColor: "#28a745",
        backgroundColor: "rgba(40, 167, 69, 0.2)",
        yAxisID: "y",
      },
      {
        label: t("vacant_rooms"),
        data: reports.map((report) => report.vacant_rooms || 0),
        borderColor: "#007bff",
        backgroundColor: "rgba(0, 123, 255, 0.2)",
        yAxisID: "y",
      },
      {
        label: t("maintenance_rooms"),
        data: reports.map((report) => report.maintenance_rooms || 0),
        borderColor: "#dc3545",
        backgroundColor: "rgba(220, 53, 69, 0.2)",
        yAxisID: "y",
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: t("number_of_rooms") },
      },
    },
    plugins: {
      legend: { position: "top" },
    },
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons px-3 py-2 bg-white border rounded">
          <div className="row align-items-center flex-nowrap">
            <div className="col-auto d-flex align-items-center gap-3">
              <h2
                className="room-title mb-0"
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  width: "400px",
                  borderBottom: "2px solid #007bff",
                  paddingBottom: "4px",
                }}
              >
                {t("room_status_report")}
              </h2>
              <DateRangePicker
                key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
                initialSettings={{
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    Today: [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("day").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("day").toDate()
                    ],
                    Yesterday: [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").startOf("day").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").endOf("day").toDate()
                    ],
                    "This Week": [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("week").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("week").toDate()
                    ],
                    "Last Week": [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "week").startOf("week").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "week").endOf("week").toDate()
                    ],
                    "This Month": [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("month").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("month").toDate()
                    ],
                    "Last Month": [
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "month").startOf("month").toDate(),
                      moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "month").endOf("month").toDate()
                    ],
                  },
                  locale: { format: "DD MMM YYYY", applyLabel: "Apply", cancelLabel: "Cancel" },
                  alwaysShowCalendars: true,
                  autoApply: false,
                  linkedCalendars: false,
                }}
                onApply={handleDateRangeChange}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control text-center fw-bold"
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    style={{
                      height: "38px",
                      fontSize: "15px",
                      borderRight: "none",
                      backgroundColor: "#ffffff",
                      width: "220px",
                    }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span
                      className="input-group-text"
                      style={{
                        backgroundColor: "#fff",
                        borderLeft: "none",
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                        height: "38px",
                      }}
                    >
                      <Calendar size={16} style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DateRangePicker>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <Popover
                content={filterContent}
                title={<span className="font-medium">{t("filters")}</span>}
                trigger="click"
                open={filterVisible}
                onOpenChange={setFilterVisible}
                placement="bottomRight"
              >

              </Popover>
              <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                  <Download size={16} className="me-2" /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="row g-4 mb-4">
          {activityStats.map((stat, index) => (
            <div
              key={index}
              className="col-md-3 col-6 stats-box text-center"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                position: "relative",
                minHeight: "100px",
                minWidth: "200px",
                background: "#fff",
                marginRight: "20px",
              }}
            >
              <div
                className="stats-line"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  backgroundColor: stat.color,
                  borderRadius: "4px 0 0 4px",
                }}
              ></div>
              <div className="stats-content" style={{ padding: "15px 10px" }}>
                <h2 style={{ fontSize: "1.8rem", margin: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 600 }}>{stat.value}</h2>
                <p style={{ margin: 0, color: "#666", fontFamily: "'Roboto', sans-serif", fontSize: "0.9rem" }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
              <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: "600", color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {t("room_status_trend")}
                </h5>
                <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
              </div>
              <div className="card-body" style={{ padding: "20px", height: "400px", width: "100%", margin: "0 auto" }}>
                {loading ? (
                  <div className="text-center my-5">
                    <div className="loader">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                ) : reports.length > 0 ? (
                  <Line data={lineChartData} options={lineChartOptions} />
                ) : (
                  <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                    <p>{t("no_data_available")}</p>
                    <p>{t("please_select_a_different_date_range")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : reports.length > 0 ? (
          <Table
            dataSource={reports}
            columns={[
              {
                title: t("report_date"),
                dataIndex: "report_date",
                key: "report_date",
                render: (text) => renderDate(text) || "-",
              },
              {
                title: t("occupied_rooms"),
                dataIndex: "occupied_rooms",
                key: "occupied_rooms",
                render: (text) => text || "0",
              },
              {
                title: t("vacant_rooms"),
                dataIndex: "vacant_rooms",
                key: "vacant_rooms",
                render: (text) => text || "0",
              },
              {
                title: t("maintenance_rooms"),
                dataIndex: "maintenance_rooms",
                key: "maintenance_rooms",
                render: (text) => text || "0",
              },
              {
                title: t("occupancy_percentage"),
                dataIndex: "occupancy_percentage",
                key: "occupancy_percentage",
                render: (text) => text ? `${text}%` : "0.00%",
              },
            ]}
            pagination={false}
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>{t("total")}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {reports.reduce((sum, r) => sum + r.occupied_rooms, 0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {reports.reduce((sum, r) => sum + r.vacant_rooms, 0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    {reports.reduce((sum, r) => sum + r.maintenance_rooms, 0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    {(
                      ((reports.reduce((sum, r) => sum + r.occupied_rooms, 0) /
                        (reports.reduce((sum, r) => sum + r.occupied_rooms, 0) +
                          reports.reduce((sum, r) => sum + r.vacant_rooms, 0) +
                          reports.reduce((sum, r) => sum + r.maintenance_rooms, 0))) || 0) * 100
                    ).toFixed(2)}%
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        ) : (
          <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
            <p>{t("no_data_available")}</p>
            <p>{t("please_select_a_different_date_range")}</p>
          </div>
        )}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={reportTitle}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={totalColumns}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />
        <style jsx>{`
          .loader {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .dot {
            width: 10px;
            height: 10px;
            background-color: #007bff;
            border-radius: 50%;
            margin: 0 5px;
            animation: dot-bounce 1.4s infinite ease-in-out both;
          }
          .dot:nth-child(1) {
            animation-delay: -0.32s;
          }
          .dot:nth-child(2) {
            animation-delay: -0.16s;
          }
          @keyframes dot-bounce {
            0%,
            80%,
            100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          .form-control {
            border-radius: 4px;
            border: 1px solid #d9d9d9;
            padding: 6px 12px;
            font-size: 14px;
          }
          .dropdown-menu .active {
            background-color: #007bff;
            color: white;
          }
        `}</style>
      </div>
    </div>
  );
};

export default RoomStatusReport;